import React, {useEffect, useMemo, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import axios from "axios";
import {COURSE_API_BASE} from "../../../../utils/apiConfig";
import {sanitizeLessonContent} from "../../../../utils/commonUtils";
import {buildScreens} from "./buildScreens";
import HeaderLesson from "./HeaderLesson";
import FooterLesson from "./FooterLesson";
import LessonScreen from "./LessonScreen";
import ContentProject from "../project/ContentProject";
import HeaderProject from "../project/HeaderProject";

const CourseContent = () => {
    const {course_id, content_id} = useParams();

    const [searchParams] = useSearchParams();
    const currentLanguage = searchParams.get("language") || "FARSI";

    const [content, setContent] = useState(null);
    const [contentType, setContentType] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
    const [completedBlocks, setCompletedBlocks] = useState({});

    /* ===================== FETCH ===================== */

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setLoading(true);
                setError("");

                const url =
                    `${COURSE_API_BASE}/${course_id}/content/${content_id}`;


                const res = await axios.get(url, {
                    params: {
                        language: currentLanguage,
                    },
                    withCredentials: true,
                });

                console.log(
                    "Content response:",
                    res.data.course.content
                );

                const row = res.data.course.content;

                const actualType = row.content?.type;

                setContentType(actualType);

                if (actualType === "lesson") {
                    setContent(
                        sanitizeLessonContent(row.content)
                    );
                } else {
                    setContent(row.content);
                }

                setCurrentScreenIndex(0);
                setCompletedBlocks({});
            } catch (err) {
                console.error("Content fetch failed:", err);
                console.error(
                    "Response:",
                    err.response?.data
                );

                setError("خطا در دریافت محتوا");
            } finally {
                setLoading(false);
            }
        };

        if (course_id && content_id) {
            fetchContent();
        }
    }, [course_id, content_id, currentLanguage]);

    /* ===================== LESSON SCREENS ===================== */

    const screens = useMemo(() => {
        if (
            contentType !== "lesson" ||
            !content?.fragments
        ) {
            return [];
        }

        return buildScreens(content.fragments);
    }, [content, contentType]);

    const currentScreen =
        screens[currentScreenIndex];

    const currentCompletedBlocks =
        completedBlocks[currentScreenIndex] ||
        new Set();

    /* ===================== LESSON COMPLETION ===================== */

    const isScreenComplete = useMemo(() => {
        if (!currentScreen) {
            return false;
        }

        const requiredBlocks =
            currentScreen.blocks.filter(
                (block) =>
                    block.type.startsWith("question_") ||
                    block.type === "interactive"
            );

        // No interactive blocks:
        // screen is immediately complete.
        if (requiredBlocks.length === 0) {
            return true;
        }

        return requiredBlocks.every((block) =>
            currentCompletedBlocks.has(block.id)
        );
    }, [
        currentScreen,
        currentCompletedBlocks
    ]);

    /* ===================== LESSON BLOCK ===================== */

    const handleBlockComplete = (blockId) => {
        setCompletedBlocks((prev) => ({
            ...prev,
            [currentScreenIndex]: new Set([
                ...(prev[currentScreenIndex] || []),
                blockId,
            ]),
        }));
    };

    /* ===================== LESSON NAVIGATION ===================== */

    const handleNext = () => {
        if (!isScreenComplete) {
            return;
        }

        if (
            currentScreenIndex + 1 >=
            screens.length
        ) {
            return;
        }

        setCurrentScreenIndex(
            (prev) => prev + 1
        );
    };

    const handlePrevious = () => {
        if (currentScreenIndex <= 0) {
            return;
        }

        setCurrentScreenIndex(
            (prev) => prev - 1
        );
    };

    /* ===================== LESSON PROGRESS ===================== */

    const progress = screens.length
        ? ((currentScreenIndex + 1) /
              screens.length) *
          100
        : 0;

    /* ===================== LOADING / ERROR ===================== */

    if (loading || error) {
        return (
            <div className="min-h-screen flex flex-col">
                <HeaderLesson/>

                <main className="flex-1 flex items-center justify-center">
                    <p>
                        {loading
                            ? "در حال بارگذاری..."
                            : error ||
                              "محتوایی برای نمایش وجود ندارد."}
                    </p>
                </main>

                <FooterLesson/>
            </div>
        );
    }

    /* ===================== PROJECT ===================== */

    if (contentType === "project") {
        return (
            <div className="min-h-screen flex flex-col">
                <HeaderProject />

                <main className="flex-1 w-full">
                    <ContentProject
                        content={content}
                    />
                </main>
            </div>
        );
    }

    /* ===================== VIDEO ===================== */

    if (contentType === "video") {
        return (
            <div className="min-h-screen flex flex-col">
                <HeaderProject/>

                <main className="flex-1 flex items-center justify-center">
                    <p>
                        ویدیو به‌زودی اضافه می‌شود.
                    </p>
                </main>

                <FooterLesson/>
            </div>
        );
    }

    /* ===================== UNKNOWN TYPE ===================== */

    if (contentType !== "lesson") {
        return (
            <div className="min-h-screen flex flex-col">
                <HeaderLesson/>

                <main className="flex-1 flex items-center justify-center">
                    <p>
                        نوع محتوا پشتیبانی نمی‌شود.
                    </p>
                </main>

                <FooterLesson/>
            </div>
        );
    }

    /* ===================== EMPTY LESSON ===================== */

    if (!currentScreen) {
        return (
            <div className="min-h-screen flex flex-col">
                <HeaderLesson/>

                <main className="flex-1 flex items-center justify-center">
                    <p>
                        محتوایی برای نمایش وجود ندارد.
                    </p>
                </main>

                <FooterLesson/>
            </div>
        );
    }

    /* ===================== LESSON ===================== */

    return (
        <div className="min-h-screen flex flex-col">
            <HeaderLesson
                progress={progress}
            />

            <main className="flex-1 w-full">
                <LessonScreen
                    screen={currentScreen}
                    completedBlocks={
                        currentCompletedBlocks
                    }
                    onBlockComplete={
                        handleBlockComplete
                    }
                />
            </main>

            <FooterLesson
                goPrevious={handlePrevious}
                goNext={handleNext}
                hasPrevious={
                    currentScreenIndex > 0
                }
                hasNext={
                    isScreenComplete
                }
            />
        </div>
    );
};

export default CourseContent;
